import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../auth/api';
import SkyDecor from '../../components/SkyDecor';
import './AuditoryExercise.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuditoryExercisePage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { childId, childName } = location.state || {};

  const [assignments, setAssignments] = useState([]);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [exerciseIds, setExerciseIds] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentExercise, setCurrentExercise] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null); // for same_or_different
  const [feedback, setFeedback] = useState(null);
  const [starsEarned, setStarsEarned] = useState(0);
  const [sessionResults, setSessionResults] = useState([]);
  const [showCelebration, setShowCelebration] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playingIndex, setPlayingIndex] = useState(null);
  const [mascotState, setMascotState] = useState('idle');
  const [startTime, setStartTime] = useState(null);
  const [shakeOption, setShakeOption] = useState(null);
  const [shakeAnswer, setShakeAnswer] = useState(null);
  const [correctGlow, setCorrectGlow] = useState(null);
  const [confettiPieces, setConfettiPieces] = useState([]);
  const [playbackSpeed, setPlaybackSpeed] = useState('normal'); // 'normal' or 'slow'

  const audioContextRef = useRef(null);

  useEffect(() => {
    if (!childId) {
      navigate('/child/hub');
      return;
    }
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    fetchAssignments();

    return () => {
      if (audioContextRef.current) audioContextRef.current.close();
    };
  }, [childId]);

  const fetchAssignments = async () => {
    try {
      const resp = await api.get(`/auditory/assignments/${childId}`);
      setAssignments(resp.data);
    } catch (err) {
      console.error('Error fetching auditory assignments:', err);
    }
  };

  const startAssignment = async (assignment) => {
    const ids = JSON.parse(assignment.exercise_ids);
    setCurrentAssignment(assignment);
    setExerciseIds(ids);
    setCurrentIndex(0);
    setStarsEarned(0);
    setSessionResults([]);
    if (ids.length > 0) {
      loadExercise(ids[0]);
    }
  };

  const loadExercise = async (id) => {
    try {
      setSelectedOption(null);
      setSelectedAnswer(null);
      setFeedback(null);
      setShakeOption(null);
      setShakeAnswer(null);
      setCorrectGlow(null);
      setMascotState('idle');
      const resp = await api.get(`/auditory/exercises/${id}`);
      setCurrentExercise(resp.data);
      setStartTime(Date.now());
    } catch (err) {
      console.error('Error loading exercise:', err);
    }
  };

  const playTone = useCallback((frequency, type, duration) => {
    if (!audioContextRef.current) return;
    const osc = audioContextRef.current.createOscillator();
    const gainNode = audioContextRef.current.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(frequency, audioContextRef.current.currentTime);
    gainNode.gain.setValueAtTime(0.1, audioContextRef.current.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + duration);
    osc.connect(gainNode);
    gainNode.connect(audioContextRef.current.destination);
    osc.start();
    osc.stop(audioContextRef.current.currentTime + duration);
  }, []);

  const playSuccessSound = useCallback(() => {
    playTone(600, 'sine', 0.1);
    setTimeout(() => playTone(800, 'sine', 0.15), 100);
    setTimeout(() => playTone(1000, 'sine', 0.2), 200);
  }, [playTone]);

  const playErrorSound = useCallback(() => {
    playTone(300, 'sawtooth', 0.3);
  }, [playTone]);

  const spawnConfetti = () => {
    const pieces = Array.from({ length: 30 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      color: ['#FFD700', '#FF6B6B', '#4DD0E1', '#CE93D8', '#AED581', '#FF8A65'][Math.floor(Math.random() * 6)],
      delay: Math.random() * 0.5,
      duration: 1 + Math.random() * 1.5,
      size: 6 + Math.random() * 8,
      rotation: Math.random() * 360
    }));
    setConfettiPieces(pieces);
    setTimeout(() => setConfettiPieces([]), 3000);
  };

  const speakWord = async (word, language = 'hi-IN', index = null) => {
    setIsPlaying(true);
    if (index !== null) setPlayingIndex(index);
    setMascotState('speaking');
    try {
      const response = await api.post('/speak-word/', { text: word, language, speed: playbackSpeed });
      if (response.data.status === 'success' && response.data.audio_data) {
        const audio = new Audio(`data:audio/wav;base64,${response.data.audio_data}`);
        audio.onended = () => { setIsPlaying(false); setPlayingIndex(null); setMascotState('idle'); };
        audio.onerror = () => { setIsPlaying(false); setPlayingIndex(null); setMascotState('idle'); };
        audio.play();
      } else {
        throw new Error('TTS failed');
      }
    } catch (err) {
      console.warn('Backend TTS failed, using browser:', err);
      if ('speechSynthesis' in window) {
        const utterance = new SpeechSynthesisUtterance(word);
        utterance.lang = language;
        if (playbackSpeed === 'slow') utterance.rate = 0.6;
        utterance.onend = () => { setIsPlaying(false); setPlayingIndex(null); setMascotState('idle'); };
        speechSynthesis.speak(utterance);
      } else {
        setIsPlaying(false);
        setPlayingIndex(null);
        setMascotState('idle');
      }
    }
  };

  // Generic option select handler (image_discrimination, audio_discrimination, sound_identification)
  const handleOptionSelect = async (option) => {
    if (feedback) return;
    setSelectedOption(option.id);
    const responseTime = startTime ? Date.now() - startTime : null;

    try {
      const result = await api.post('/auditory/submit-answer', {
        child_id: childId,
        exercise_id: currentExercise.id,
        selected_option_id: option.id,
        response_time_ms: responseTime
      });

      const data = result.data;
      setFeedback(data);

      if (data.is_correct) {
        setCorrectGlow(option.id);
        setStarsEarned(prev => prev + 1);
        setMascotState('success');
        playSuccessSound();
        spawnConfetti();
      } else {
        setShakeOption(option.id);
        setMascotState('error');
        playErrorSound();
        setTimeout(() => setCorrectGlow(data.correct_option_id), 600);
      }

      setSessionResults(prev => [...prev, {
        exercise_id: currentExercise.id,
        target_word: currentExercise.target_word,
        is_correct: data.is_correct,
        response_time_ms: responseTime
      }]);
    } catch (err) {
      console.error('Error submitting answer:', err);
    }
  };

  // Same/Different answer handler — FIXED LOGIC
  const handleSameOrDifferentAnswer = async (userSaidSame) => {
    if (feedback) return;
    const responseTime = startTime ? Date.now() - startTime : null;

    // FIXED: The first option being correct means the sounds ARE the same
    // (matches therapist form: "mark correct if they ARE the same")
    const firstOption = currentExercise.options[0];
    const soundsAreSame = firstOption?.is_correct === true;
    const isCorrect = userSaidSame === soundsAreSame;

    // Submit the option that aligns with the user's choice
    const optionToSubmit = userSaidSame ? currentExercise.options[0] : currentExercise.options[1];

    try {
      const result = await api.post('/auditory/submit-answer', {
        child_id: childId,
        exercise_id: currentExercise.id,
        selected_option_id: optionToSubmit?.id || currentExercise.options[0].id,
        response_time_ms: responseTime
      });

      const data = result.data;
      setFeedback(data);
      setSelectedAnswer(userSaidSame ? 'same' : 'different');

      if (data.is_correct) {
        setCorrectGlow(userSaidSame ? 'same' : 'different');
        setStarsEarned(prev => prev + 1);
        setMascotState('success');
        playSuccessSound();
        spawnConfetti();
      } else {
        setShakeAnswer(userSaidSame ? 'same' : 'different');
        setMascotState('error');
        playErrorSound();
        setTimeout(() => setCorrectGlow(!userSaidSame ? 'same' : 'different'), 600);
      }

      setSessionResults(prev => [...prev, {
        exercise_id: currentExercise.id,
        target_word: currentExercise.target_word,
        is_correct: data.is_correct,
        response_time_ms: responseTime
      }]);
    } catch (err) {
      console.error('Error submitting answer:', err);
    }
  };

  const handleNext = () => {
    const nextIndex = currentIndex + 1;
    if (nextIndex < exerciseIds.length) {
      setCurrentIndex(nextIndex);
      loadExercise(exerciseIds[nextIndex]);
    } else {
      setShowCelebration(true);
      playSuccessSound();
    }
  };

  const closeCelebration = () => {
    setShowCelebration(false);
    setCurrentAssignment(null);
    setCurrentExercise(null);
  };

  const getMascot = () => {
    switch (mascotState) {
      case 'speaking': return '🔊';
      case 'success': return '🎉';
      case 'error': return '😕';
      default: return '🐻';
    }
  };

  const getExerciseTypeBadge = (type) => {
    switch (type) {
      case 'image_discrimination': return { icon: '🖼️', label: 'Image Match', color: '#E1F5FE' };
      case 'audio_discrimination': return { icon: '🔊', label: 'Audio Match', color: '#F3E5F5' };
      case 'same_or_different': return { icon: '🔀', label: 'Same or Different', color: '#FFF3E0' };
      case 'sound_identification': return { icon: '🔍', label: 'Sound ID', color: '#E8F5E9' };
      default: return { icon: '👂', label: 'Auditory', color: '#E0E0E0' };
    }
  };

  const getInstruction = () => {
    if (!currentExercise) return '';
    if (currentExercise.instruction_text) return currentExercise.instruction_text;
    switch (currentExercise.exercise_type) {
      case 'image_discrimination':
        return '🔊 Listen to the word, then pick the right picture!';
      case 'audio_discrimination':
        return '👀 Look at the picture, then pick the right sound!';
      case 'same_or_different':
        return '🔊 Listen to both sounds — are they the same or different?';
      case 'sound_identification':
        return '🔊 Listen to the sound, then pick the right picture!';
      default:
        return '🔊 Listen carefully and choose the correct answer!';
    }
  };

  // Speed toggle component
  const SpeedToggle = () => (
    <div className="aud-speed-toggle">
      <button
        className={`aud-speed-btn ${playbackSpeed === 'normal' ? 'aud-speed-active' : ''}`}
        onClick={() => setPlaybackSpeed('normal')}
        title="Normal speed"
      >
        🐇 Normal
      </button>
      <button
        className={`aud-speed-btn ${playbackSpeed === 'slow' ? 'aud-speed-active' : ''}`}
        onClick={() => setPlaybackSpeed('slow')}
        title="Slow speed"
      >
        🐢 Slow
      </button>
    </div>
  );

  // No child selected
  if (!childId) return null;

  // Assignment selection screen
  if (!currentExercise) {
    return (
      <div className="aud-container sb-page">
        <SkyDecor />
        <div className="aud-header">
          <button className="aud-back-btn" onClick={() => navigate('/child/hub')}>← Back</button>
          <div className="aud-header-title">👂 Auditory Exercises</div>
          <div></div>
        </div>

        <div className="aud-mission-select">
          <div className="aud-mascot-big">🐻</div>
          <h2 className="aud-section-title">Choose your mission, {childName}!</h2>

          <div className="aud-mission-grid">
            {assignments.length > 0 ? [...assignments]
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
              .map(assignment => {
              const ids = JSON.parse(assignment.exercise_ids);
              return (
                <div key={assignment.id} className="aud-mission-card" onClick={() => startAssignment(assignment)}>
                  <div className="aud-mission-icon">🚀</div>
                  <h3>Mission #{assignment.id}</h3>
                  <p>📚 {ids.length} exercises</p>
                  {assignment.created_at && (
                    <p className="aud-mission-date">📅 {new Date(assignment.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                  )}
                  <p className="aud-mission-from">From: {assignment.assigned_by}</p>
                  <div className="aud-mission-go">Let's Go! →</div>
                </div>
              );
            }) : (
              <div className="aud-empty-state">
                <div className="aud-empty-icon">🎒</div>
                <p>No missions yet! Ask your teacher.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Determine exercise type
  const exerciseType = currentExercise.exercise_type;
  const typeBadge = getExerciseTypeBadge(exerciseType);

  return (
    <div className="aud-container sb-page">
      <SkyDecor />
      {/* Confetti */}
      {confettiPieces.length > 0 && (
        <div className="aud-confetti-container">
          {confettiPieces.map(p => (
            <div
              key={p.id}
              className="aud-confetti-piece"
              style={{
                left: `${p.left}%`,
                backgroundColor: p.color,
                animationDelay: `${p.delay}s`,
                animationDuration: `${p.duration}s`,
                width: `${p.size}px`,
                height: `${p.size}px`,
                transform: `rotate(${p.rotation}deg)`
              }}
            />
          ))}
        </div>
      )}

      {/* Celebration overlay */}
      {showCelebration && (
        <div className="aud-celebration-overlay">
          <div className="aud-celebration-content">
            <div className="aud-trophy">🏆</div>
            <h1>Mission Complete!</h1>
            <div className="aud-celebration-stars">
              {Array.from({ length: exerciseIds.length }).map((_, i) => (
                <span key={i} className={`aud-star ${i < starsEarned ? 'aud-star-earned' : ''}`}>
                  {i < starsEarned ? '⭐' : '☆'}
                </span>
              ))}
            </div>
            <p className="aud-celebration-score">
              You got {starsEarned} out of {exerciseIds.length} correct!
            </p>
            <button className="aud-play-more-btn" onClick={closeCelebration}>Play More! 🎮</button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="aud-header">
        <button className="aud-back-btn" onClick={() => { setCurrentExercise(null); setCurrentAssignment(null); }}>
          ← Back
        </button>
        <div className="aud-progress-bar">
          {exerciseIds.map((_, i) => (
            <div key={i} className={`aud-progress-dot ${i < currentIndex ? 'completed' : ''} ${i === currentIndex ? 'current' : ''}`}>
              {i < currentIndex ? '⭐' : i === currentIndex ? '●' : '○'}
            </div>
          ))}
        </div>
        <div className="aud-star-count">⭐ {starsEarned}</div>
      </div>

      {/* Game Area */}
      <div className="aud-game-area">
        {/* Mascot + Instruction */}
        <div className="aud-instruction-bar">
          <div className="aud-mascot">{getMascot()}</div>
          <div className="aud-instruction-bubble">
            {getInstruction()}
          </div>
        </div>

        {/* Exercise Type Badge */}
        <div className="aud-type-badge" style={{ background: typeBadge.color }}>
          <span>{typeBadge.icon}</span>
          <span>{typeBadge.label}</span>
        </div>

        {/* Speed Toggle */}
        <SpeedToggle />

        {/* ========================================== */}
        {/* IMAGE DISCRIMINATION: Hear word → pick image */}
        {/* ========================================== */}
        {exerciseType === 'image_discrimination' && (
          <>
            <div className="aud-prompt-section">
              <button
                className={`aud-play-btn ${isPlaying ? 'aud-playing' : ''}`}
                onClick={() => speakWord(currentExercise.target_word, currentExercise.language)}
                disabled={isPlaying}
              >
                <span className="aud-play-icon">{isPlaying ? '🔊' : '▶️'}</span>
                <span>{isPlaying ? 'Playing...' : 'Play Sound'}</span>
              </button>
            </div>

            <div className="aud-options-grid aud-image-options">
              {currentExercise.options.map(option => (
                <div
                  key={option.id}
                  className={`aud-option-card aud-image-card
                    ${selectedOption === option.id ? 'aud-selected' : ''}
                    ${shakeOption === option.id ? 'aud-shake' : ''}
                    ${correctGlow === option.id ? 'aud-correct-glow' : ''}
                    ${feedback && !option.is_correct && selectedOption === option.id ? 'aud-wrong' : ''}
                  `}
                  onClick={() => handleOptionSelect(option)}
                >
                  <div className="aud-option-image">
                    {option.image_url ? (
                      <img
                        src={option.image_url.startsWith('http') ? option.image_url : `${BACKEND_URL}${option.image_url}`}
                        alt={option.option_text}
                      />
                    ) : (
                      <div className="aud-option-placeholder">{option.option_text.charAt(0)}</div>
                    )}
                  </div>
                  <div className="aud-option-label">{option.option_text}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ========================================== */}
        {/* AUDIO DISCRIMINATION: See image → pick audio */}
        {/* ========================================== */}
        {exerciseType === 'audio_discrimination' && (
          <>
            <div className="aud-prompt-section">
              <div className="aud-target-image-container">
                {currentExercise.target_image_url ? (
                  <img
                    src={currentExercise.target_image_url.startsWith('http')
                      ? currentExercise.target_image_url
                      : `${BACKEND_URL}${currentExercise.target_image_url}`}
                    alt="What is this?"
                    className="aud-target-image"
                  />
                ) : (
                  <div className="aud-target-placeholder">{currentExercise.target_word.charAt(0)}</div>
                )}
              </div>
            </div>

            <div className="aud-options-grid aud-audio-options">
              {currentExercise.options.map(option => (
                <button
                  key={option.id}
                  className={`aud-option-card aud-audio-card
                    ${selectedOption === option.id ? 'aud-selected' : ''}
                    ${shakeOption === option.id ? 'aud-shake' : ''}
                    ${correctGlow === option.id ? 'aud-correct-glow' : ''}
                    ${feedback && !option.is_correct && selectedOption === option.id ? 'aud-wrong' : ''}
                  `}
                  onClick={() => {
                    if (!feedback) {
                      speakWord(option.option_text, currentExercise.language);
                    }
                    handleOptionSelect(option);
                  }}
                >
                  <div className="aud-audio-icon">🔊</div>
                  <div className="aud-option-label">{option.option_text}</div>
                </button>
              ))}
            </div>
          </>
        )}

        {/* ========================================== */}
        {/* SAME OR DIFFERENT: Listen to two sounds → Same/Different */}
        {/* ========================================== */}
        {exerciseType === 'same_or_different' && (
          <>
            <div className="aud-sounds-section">
              <div className="aud-sound-pair">
                <button
                  className={`aud-sound-btn aud-sound-1 ${playingIndex === 0 ? 'aud-sound-playing' : ''}`}
                  onClick={() => !isPlaying && speakWord(currentExercise.options[0]?.option_text, currentExercise.language, 0)}
                  disabled={isPlaying}
                >
                  <span className="aud-sound-label">Sound 1</span>
                  <span className="aud-sound-icon">{playingIndex === 0 ? '🔊' : '▶️'}</span>
                  <span className="aud-sound-text">{playingIndex === 0 ? 'Playing...' : 'Tap to hear'}</span>
                </button>

                <div className="aud-vs-badge">VS</div>

                <button
                  className={`aud-sound-btn aud-sound-2 ${playingIndex === 1 ? 'aud-sound-playing' : ''}`}
                  onClick={() => !isPlaying && speakWord(currentExercise.options[1]?.option_text, currentExercise.language, 1)}
                  disabled={isPlaying}
                >
                  <span className="aud-sound-label">Sound 2</span>
                  <span className="aud-sound-icon">{playingIndex === 1 ? '🔊' : '▶️'}</span>
                  <span className="aud-sound-text">{playingIndex === 1 ? 'Playing...' : 'Tap to hear'}</span>
                </button>
              </div>
            </div>

            <div className="aud-answer-section">
              <button
                className={`aud-answer-btn aud-same-btn
                  ${selectedAnswer === 'same' ? 'aud-selected' : ''}
                  ${shakeAnswer === 'same' ? 'aud-shake' : ''}
                  ${correctGlow === 'same' ? 'aud-correct-glow' : ''}
                  ${feedback && selectedAnswer === 'same' && !feedback.is_correct ? 'aud-wrong' : ''}
                `}
                onClick={() => handleSameOrDifferentAnswer(true)}
                disabled={!!feedback}
              >
                <span className="aud-answer-icon">✅</span>
                <span className="aud-answer-text">Same!</span>
              </button>

              <button
                className={`aud-answer-btn aud-diff-btn
                  ${selectedAnswer === 'different' ? 'aud-selected' : ''}
                  ${shakeAnswer === 'different' ? 'aud-shake' : ''}
                  ${correctGlow === 'different' ? 'aud-correct-glow' : ''}
                  ${feedback && selectedAnswer === 'different' && !feedback.is_correct ? 'aud-wrong' : ''}
                `}
                onClick={() => handleSameOrDifferentAnswer(false)}
                disabled={!!feedback}
              >
                <span className="aud-answer-icon">❌</span>
                <span className="aud-answer-text">Different!</span>
              </button>
            </div>
          </>
        )}

        {/* ========================================== */}
        {/* SOUND IDENTIFICATION: Listen → pick matching image */}
        {/* ========================================== */}
        {exerciseType === 'sound_identification' && (
          <>
            <div className="aud-prompt-section">
              <button
                className={`aud-play-btn ${isPlaying ? 'aud-playing' : ''}`}
                onClick={() => speakWord(currentExercise.target_word, currentExercise.language)}
                disabled={isPlaying}
              >
                <span className="aud-play-icon">{isPlaying ? '🔊' : '▶️'}</span>
                <span>{isPlaying ? 'Playing...' : 'Play Sound'}</span>
              </button>
            </div>

            <div className="aud-options-grid aud-image-options">
              {currentExercise.options.map(option => (
                <div
                  key={option.id}
                  className={`aud-option-card aud-image-card
                    ${selectedOption === option.id ? 'aud-selected' : ''}
                    ${shakeOption === option.id ? 'aud-shake' : ''}
                    ${correctGlow === option.id ? 'aud-correct-glow' : ''}
                    ${feedback && !option.is_correct && selectedOption === option.id ? 'aud-wrong' : ''}
                  `}
                  onClick={() => handleOptionSelect(option)}
                >
                  <div className="aud-option-image">
                    {option.image_url ? (
                      <img
                        src={option.image_url.startsWith('http') ? option.image_url : `${BACKEND_URL}${option.image_url}`}
                        alt={option.option_text}
                      />
                    ) : (
                      <div className="aud-option-placeholder">{option.option_text.charAt(0).toUpperCase()}</div>
                    )}
                  </div>
                  <div className="aud-option-label">{option.option_text}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Feedback */}
        {feedback && (
          <div className={`aud-feedback-bar ${feedback.is_correct ? 'aud-feedback-correct' : 'aud-feedback-wrong'}`}>
            <div className="aud-feedback-text">{feedback.message}</div>
            <button className="aud-next-btn" onClick={handleNext}>
              {currentIndex < exerciseIds.length - 1 ? 'Next →' : '🏆 Finish!'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditoryExercisePage;
